<template>
  <div class="min-h-screen bg-white">
    <TrustBar />
    <div class="pt-18"></div>

    <!-- Loading State -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div
          class="inline-block w-12 h-12 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="mt-4 text-gray-600">Chargement...</p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="min-h-screen flex items-center justify-center"
    >
      <div class="text-center">
        <div class="text-7xl mb-6">⚠️</div>
        <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ error }}</h3>
        <router-link
          to="/cards/shopping-gift-cards"
          class="inline-block px-6 py-3 bg-fuchsia-600 text-white rounded-xl hover:bg-fuchsia-700 transition"
        >
          Retour aux cartes cadeaux
        </router-link>
      </div>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Breadcrumb -->
      <div class="bg-gray-50 py-3 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="flex text-sm">
            <router-link to="/" class="text-gray-500 hover:text-gray-900"
              >Accueil</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <router-link
              to="/cards/shopping-gift-cards"
              class="text-gray-500 hover:text-gray-900"
              >Cartes cadeaux</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <span class="text-gray-900 font-medium">{{ card?.title }}</span>
          </nav>
        </div>
      </div>

      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="grid gap-8 lg:gap-12 lg:grid-cols-12 lg:mt-0">
          <!-- Left Column: Product Image -->
          <div class="lg:col-span-5 relative lg:mt-12 mt-8">
            <div
              class="flex justify-center lg:flex-col gap-4 lg:sticky lg:top-32"
            >
              <div class="relative">
                <!-- Gradient background effect -->
                <div
                  class="absolute right-1/4 top-1/2 -translate-y-1/2 h-[calc(100%+3.5rem)] w-[200vw] -z-10 rounded-full lg:h-[calc(100%+10.5rem)] lg:-top-8 lg:translate-y-0 bg-gradient-to-r from-gray-100 via-gray-100 to-transparent/5"
                ></div>
                <div v-if="card?.imageUrl" class="relative">
                  <img
                    :src="card.imageUrl"
                    :alt="card.title"
                    class="rounded-xl relative h-36 md:h-48 lg:h-64 w-auto lg:w-[400px] shadow-[0_0_4px_rgba(0,0,0,0.25)] object-cover"
                  />
                </div>
                <div
                  v-else
                  class="relative px-12 h-36 md:h-48 lg:h-64 w-auto lg:w-[400px] rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 shadow-[0_0_4px_rgba(0,0,0,0.25)] flex items-center justify-center"
                >
                  <svg
                    class="w-20 h-20 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
              </div>
              <!-- Stock Badge -->
              <div class="text-center">
                <span
                  class="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                  :class="stockStatusClass"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {{ stockStatusText }}
                </span>
              </div>
              <!-- USP Badges (Desktop) -->
              <div class="hidden lg:block relative">
                <ul class="flex flex-wrap gap-2 items-center lg:items-start">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Livraison en ligne instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Right Column: Form -->
          <div
            class="lg:col-span-7 flex flex-1 flex-col items-center lg:items-start mt-3 lg:mt-2"
          >
            <div class="w-full flex flex-1 flex-col mb-5 lg:mb-6">
              <form @submit.prevent="goToCheckout" class="flex flex-col">
                <!-- Title -->
                <div class="flex flex-row flex-wrap gap-x-2 items-center">
                  <h1 class="font-bold text-xl md:text-3xl mb-2">
                    {{ card?.title }}
                  </h1>
                </div>

                <p v-if="card?.description" class="text-gray-600 mb-6">
                  {{ card.description }}
                </p>

                <!-- Price Display (Fixed for custom cards) -->
                <div class="mb-6">
                  <div
                    class="inline-flex items-center gap-4 bg-white border-2 border-gray-200 rounded-2xl px-6 py-4"
                  >
                    <span class="text-sm font-semibold text-gray-700"
                      >Prix</span
                    >
                    <span class="text-3xl font-bold text-fuchsia-600">{{
                      formatPrice(card?.price, card?.currency)
                    }}</span>
                  </div>
                </div>

                <!-- Quantity Selector -->
                <div class="mb-6">
                  <div
                    class="inline-flex items-center gap-4 bg-white border-2 border-gray-200 rounded-2xl px-4 py-3 hover:border-gray-300 transition-colors duration-200"
                  >
                    <span class="text-sm font-semibold text-gray-700"
                      >Quantité</span
                    >
                    <div class="flex items-center gap-2">
                      <button
                        type="button"
                        @click="decrementQuantity"
                        :disabled="quantity <= 1"
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
                      >
                        <svg
                          class="w-4 h-4 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <span
                        class="font-bold text-lg min-w-[28px] text-center text-gray-900"
                        >{{ quantity }}</span
                      >
                      <button
                        type="button"
                        @click="incrementQuantity"
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-fuchsia-100 hover:bg-fuchsia-200 active:bg-fuchsia-300 transition-all"
                      >
                        <svg
                          class="w-4 h-4 text-fuchsia-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Buy Button -->
                <div class="mb-6">
                  <button
                    type="submit"
                    class="w-full rounded-2xl flex justify-center items-center bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-700 hover:to-pink-700 text-white min-h-14 h-14 font-bold text-base transition-all duration-200 shadow-lg shadow-fuchsia-200 hover:shadow-xl hover:shadow-fuchsia-300 active:scale-[0.98]"
                  >
                    Acheter • {{ formatPrice(calculateTotal(), card?.currency) }}
                  </button>
                </div>

                <!-- Payment Methods -->
                <div
                  class="flex flex-col gap-4 items-center justify-centerlg:items-start mb-6"
                >
                  <div class="flex items-center gap-2">
                    <ul class="flex gap-2 p-0 m-0 list-none">
                      <li
                        class="border border-gray-200 rounded-sm p-1 bg-white flex items-center justify-center w-9 h-7"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="10"
                            font-weight="bold"
                            fill="#1A1F71"
                          >
                            VISA
                          </text>
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-sm p-1 bg-white flex items-center justify-center w-9 h-7"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <circle cx="12" cy="8" r="6" fill="#EB001B" />
                          <circle cx="24" cy="8" r="6" fill="#F79E1B" />
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-sm p-1 bg-white flex items-center justify-center w-9 h-7"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="8"
                            font-weight="bold"
                            fill="#003087"
                          >
                            PayPal
                          </text>
                        </svg>
                      </li>
                    </ul>
                  </div>
                </div>
              </form>

              <!-- USP Badges (Mobile) -->
              <div class="lg:hidden">
                <ul class="flex flex-wrap gap-2 items-center">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Livraison en ligne instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>

        <!-- About Section -->
        <aside class="lg:col-span-10 lg:col-start-2 mt-8 lg:mt-10">
          <div class="grid gap-8 lg:gap-12 lg:grid-cols-12">
            <div class="lg:col-span-5">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-4">
                  À propos de {{ card?.title }}
                </h2>
                <div class="text-gray-600 space-y-4">
                  <p>
                    Achetez un code de la carte cadeau {{ card?.title }} en
                    ligne. Prix fixe de {{ formatPrice(card?.price, card?.currency) }}.
                  </p>
                  <p>
                    Vous recevrez votre code par e-mail dans les 30 secondes.
                    C'est rapide, sûr et simple.
                  </p>
                </div>
              </section>
              <div class="mt-4 lg:mt-4 text-sm text-gray-500">
                <span>En utilisant ce service, vous acceptez les </span>
                <a href="#" class="underline hover:text-gray-900"
                  >Conditions générales</a
                >
                <span> de {{ card?.title }}.</span>
              </div>
            </div>

            <!-- FAQ Section -->
            <div class="lg:col-span-7 flex flex-col">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-5">
                  Questions fréquemment posées
                </h2>
                <div class="space-y-3">
                  <!-- FAQ Item 1 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 0 }"
                  >
                    <button
                      @click="toggleFaq(0)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment utiliser ma carte cadeau {{ card?.title }}?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 0
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 0 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 0" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Utilisez votre code cadeau sur la plateforme de
                        {{ card?.title }} pour recharger votre compte ou
                        effectuer des achats.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 2 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 1 }"
                  >
                    <button
                      @click="toggleFaq(1)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Combien de temps le code est-il valide?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 1
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 1 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 1" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        La validité de votre code cadeau dépend des conditions
                        de {{ card?.title }}. Veuillez consulter leurs
                        conditions générales.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 3 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 2 }"
                  >
                    <button
                      @click="toggleFaq(2)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment recevoir mon code?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 2
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 2 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 2" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Après votre paiement, vous recevrez votre code par
                        e-mail dans les 30 secondes.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </aside>
      </main>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const { $constants } = proxy;

// State
const card = ref(null);
const loading = ref(true);
const error = ref(null);
const quantity = ref(1);

// FAQ state
const openFaq = ref(null);

const toggleFaq = (index) => {
  openFaq.value = openFaq.value === index ? null : index;
};

// Stock status
const stockStatusClass = computed(() => {
  if (!card.value) return 'bg-gray-100 text-gray-700';
  if (card.value.stockQuantity === 0) return 'bg-red-100 text-red-700';
  if (card.value.stockQuantity < 5) return 'bg-amber-100 text-amber-700';
  return 'bg-green-100 text-green-700';
});

const stockStatusText = computed(() => {
  if (!card.value) return 'Chargement...';
  if (card.value.stockQuantity === 0) return 'Rupture de stock';
  if (card.value.stockQuantity < 5)
    return `Plus que ${card.value.stockQuantity} en stock`;
  return `${card.value.stockQuantity} disponibles`;
});

const formatPrice = (price, currency = 'XOF') => {
  if (!price) return '0';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(price);
};

const calculateTotal = () => {
  if (!card.value) return 0;
  return card.value.price * quantity.value;
};

const incrementQuantity = () => {
  quantity.value++;
};

const decrementQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--;
  }
};

const fetchCard = async () => {
  const cardId = route.params.id;
  loading.value = true;
  error.value = null;

  try {
    const response = await fetch(
      `${$constants.ENDPOINTS.LOCAL_API}/custom-cards/${cardId}`
    );
    const data = await response.json();

    if (response.ok) {
      card.value = data.data || data;
    } else {
      error.value = data.message || 'Carte non trouvée';
    }
  } catch (err) {
    error.value = 'Erreur de connexion au serveur';
    console.error('Fetch card error:', err);
  } finally {
    loading.value = false;
  }
};

const goToCheckout = () => {
  if (!card.value) {
    return;
  }

  const price = calculateTotal();

  router.push({
    path: '/checkout/quick',
    query: {
      productId: card.value?.id,
      amount: price,
      orderType: 'custom_card',
      countryCode: 'BJ'
    }
  });
};

onMounted(() => {
  fetchCard();
});
</script>

<style scoped>
/* Custom transitions for FAQ */
.bg-gray-100 > div {
  transition: all 0.3s ease;
}
</style>
